/**
 * Reads the real-world length a reviewer types when calibrating a page scale (Issue #2442).
 *
 * A calibration is only as good as the number it is built on, so this parser accepts a small set
 * of unambiguous spellings and rejects everything else with a message that says what to change. A
 * unit is always required: a bare `11` could be feet, inches or metres, and guessing would silently
 * produce a scale that is wrong by a factor of twelve.
 *
 * Accepted forms, with any spacing and in any letter case:
 *
 * - feet: `11'`, `11.5 ft`, `11 feet`
 * - feet and inches: `11'-0"`, `11' 6"`, `11'-6 1/2"`, `11 ft 6 in`
 * - inches: `132"`, `132 in`, `5 1/2"`, `3/4 inch`
 * - metric: `3.35 m`, `335 cm`, `3350 mm`
 *
 * The result is always in inches, the unit the measurement pipeline works in, using the exact
 * definition of 25.4 millimetres per inch.
 *
 * The server has a parser with the same rules (`KnownLengthParser.java`) and is the authority for
 * what is stored. This one exists so the reviewer sees problems while typing. The two are tested
 * with the same accepted and rejected spellings.
 */

/** Exact number of millimetres in one inch. */
export const MILLIMETERS_PER_INCH = 25.4;

/** Longest accepted length, one million feet; anything larger is a typing mistake. */
export const MAX_KNOWN_LENGTH_INCHES = 12_000_000;

/** The unit family a length was typed in. */
export type KnownLengthUnit = 'inch' | 'foot' | 'foot-inch' | 'millimeter' | 'centimeter' | 'meter';

/** A typed length, understood. */
export interface ParsedKnownLength {
  ok: true;
  /** The length in inches. */
  inches: number;
  unit: KnownLengthUnit;
  /** The length rewritten in a standard form, e.g. `11'-0"`. */
  normalized: string;
  /** Whether the reviewer typed a metric unit. */
  metric: boolean;
}

/** A typed length that cannot be used, with a message written for the reviewer. */
export interface RejectedKnownLength {
  ok: false;
  message: string;
}

const NUMBER = String.raw`(\d+(?:\.\d+)?|\.\d+)`;
const FEET_MARK = String.raw`(?:'|ft\.?|feet|foot)`;
const INCH_MARK = String.raw`(?:"|in\.?|inch|inches)`;
/** Whole inches with a fraction ("6 1/2" or "6-1/2"), a fraction alone, or a decimal. */
const INCH_VALUE = String.raw`(?:(\d+)[ -](\d+)\/(\d+)|(\d+)\/(\d+)|${NUMBER})`;

const FEET_AND_INCHES = new RegExp(String.raw`^(\d+)\s*${FEET_MARK}\s*-?\s*${INCH_VALUE}\s*${INCH_MARK}$`);
const FEET_ONLY = new RegExp(String.raw`^${NUMBER}\s*${FEET_MARK}$`);
const INCHES_ONLY = new RegExp(String.raw`^${INCH_VALUE}\s*${INCH_MARK}$`);
const METRIC = new RegExp(
  String.raw`^${NUMBER}\s*(mm|cm|m|millimeters?|millimetres?|centimeters?|centimetres?|meters?|metres?)$`
);
const BARE_NUMBER = new RegExp(String.raw`^${NUMBER}$`);
const FEET_THEN_BARE_INCHES = new RegExp(String.raw`^\d+\s*${FEET_MARK}\s*-?\s*[\d./ -]+$`);

const GREATER_THAN_ZERO = 'The length must be greater than zero.';

/** Parses a typed length. Never throws. */
export function parseKnownLength(text: string | null | undefined): ParsedKnownLength | RejectedKnownLength {
  const value = canonicalText(text);
  if (!value) {
    return reject('Enter the length printed on the plan, with its unit.');
  }

  const feetAndInches = FEET_AND_INCHES.exec(value);
  if (feetAndInches) {
    const inches = inchValue(feetAndInches, 2);
    if (typeof inches === 'string') return reject(inches);
    if (inches >= 12) {
      return reject(
        'In feet and inches the inches must be less than 12. Write the length as feet and the remaining inches, or in inches only.'
      );
    }
    return imperial(Number(feetAndInches[1]) * 12 + inches, 'foot-inch');
  }

  const feetOnly = FEET_ONLY.exec(value);
  if (feetOnly) {
    return imperial(Number(feetOnly[1]) * 12, 'foot');
  }

  const inchesOnly = INCHES_ONLY.exec(value);
  if (inchesOnly) {
    const inches = inchValue(inchesOnly, 1);
    if (typeof inches === 'string') return reject(inches);
    return imperial(inches, 'inch');
  }

  const metric = METRIC.exec(value);
  if (metric) {
    return metricLength(Number(metric[1]), metric[2]);
  }

  return reject(explainRejection(value));
}

/**
 * Lower-cases the text, collapses spacing, and replaces typographic foot and inch marks with the
 * plain ones, so that a length pasted from a document parses like one typed by hand.
 */
function canonicalText(text: string | null | undefined): string {
  return (text ?? '')
    .trim()
    .toLowerCase()
    .replace(/[′’‘]/g, "'")
    .replace(/[″”“]/g, '"')
    .replace(/''/g, '"')
    .replace(/\s+/g, ' ');
}

/** Reads the six capture groups of INCH_VALUE starting at `first`; returns a message on failure. */
function inchValue(match: RegExpExecArray, first: number): number | string {
  if (match[first] !== undefined) {
    const fraction = fractionValue(match[first + 1], match[first + 2]);
    if (typeof fraction === 'string') return fraction;
    if (fraction >= 1) {
      return 'The fraction after the whole inches must be less than one, for example 6 1/2".';
    }
    return Number(match[first]) + fraction;
  }
  if (match[first + 3] !== undefined) {
    return fractionValue(match[first + 3], match[first + 4]);
  }
  return Number(match[first + 5]);
}

/** Divides a fraction's two parts; returns a message when the denominator is zero. */
function fractionValue(numerator: string, denominator: string): number | string {
  const bottom = Number(denominator);
  if (bottom === 0) return 'A fraction cannot be divided by zero.';
  return Number(numerator) / bottom;
}

/** Builds the result for a length typed in feet or inches, written back in feet and inches. */
function imperial(inches: number, unit: KnownLengthUnit): ParsedKnownLength | RejectedKnownLength {
  return checkRange(inches) ?? { ok: true, inches, unit, normalized: normalizeImperial(inches), metric: false };
}

/** Builds the result for a metric length, written back in the unit the reviewer used. */
function metricLength(amount: number, unitText: string): ParsedKnownLength | RejectedKnownLength {
  let unit: KnownLengthUnit;
  let millimeters: number;
  let symbol: string;
  if (unitText.startsWith('mm') || unitText.startsWith('milli')) {
    unit = 'millimeter';
    millimeters = amount;
    symbol = 'mm';
  } else if (unitText.startsWith('c')) {
    unit = 'centimeter';
    millimeters = amount * 10;
    symbol = 'cm';
  } else {
    unit = 'meter';
    millimeters = amount * 1000;
    symbol = 'm';
  }
  const inches = millimeters / MILLIMETERS_PER_INCH;
  return checkRange(inches) ?? { ok: true, inches, unit, normalized: `${trimmed(amount)} ${symbol}`, metric: true };
}

/** Rejects a length that is zero, negative, not a number, or too large for a plan. */
function checkRange(inches: number): RejectedKnownLength | null {
  if (!Number.isFinite(inches) || inches <= 0) return reject(GREATER_THAN_ZERO);
  if (inches > MAX_KNOWN_LENGTH_INCHES) return reject('This length is too large to be a dimension on a plan.');
  return null;
}

/**
 * Writes inches the way dimensions are written on drawings: `11'-0"`, `4'-9 3/4"`, `6 1/2"`. A
 * length that is not a whole number of sixteenths is written as decimal inches instead, so the
 * standard form never rounds what the reviewer typed.
 */
export function normalizeImperial(inches: number): string {
  const sixteenthsExact = inches * 16;
  const sixteenths = Math.round(sixteenthsExact);
  if (Math.abs(sixteenthsExact - sixteenths) > 1e-6) {
    return `${trimmed(inches)} in`;
  }
  const feet = Math.floor(sixteenths / 192);
  const remainder = sixteenths % 192;
  const wholeInches = Math.floor(remainder / 16);
  let numerator = remainder % 16;
  let denominator = 16;
  while (numerator !== 0 && numerator % 2 === 0) {
    numerator /= 2;
    denominator /= 2;
  }
  const fraction = numerator === 0 ? '' : `${numerator}/${denominator}`;
  let inchPart: string;
  if (!fraction) {
    inchPart = `${wholeInches}"`;
  } else if (wholeInches === 0 && feet === 0) {
    inchPart = `${fraction}"`;
  } else {
    inchPart = `${wholeInches} ${fraction}"`;
  }
  return feet > 0 ? `${feet}'-${inchPart}` : inchPart;
}

/** Writes a number with at most four decimals and no trailing zeros. */
function trimmed(value: number): string {
  return String(Number(value.toFixed(4)));
}

/** Chooses the most helpful explanation for text that matched none of the accepted forms. */
function explainRejection(value: string): string {
  if (value.startsWith('-')) return GREATER_THAN_ZERO;
  if (value.includes(',')) return 'Use a decimal point and no thousands separators, for example 3.35 m.';
  if (BARE_NUMBER.test(value)) {
    return "Add a unit, for example 11' or 3.35 m. A number on its own could be feet, inches or metres.";
  }
  if (FEET_THEN_BARE_INCHES.test(value)) {
    return 'Add the inch mark after the inches, for example 11\'-6".';
  }
  return "This length is not in a supported format. Try 11', 11'-6\", 132 in, 3.35 m, 335 cm or 3350 mm.";
}

/** Wraps a message as a rejected length. */
function reject(message: string): RejectedKnownLength {
  return { ok: false, message };
}
